import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Marker } from '@react-google-maps/api';
import { loadJSON } from '../utils/dataLoader';
import { useAppState } from '../state/store';
import type { Stop, DayLeg } from '../types';
import './Map.css';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

interface MapProps {
  className?: string;
  onMapReady?: (map: google.maps.Map) => void;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 31.7769,
  lng: 35.2224, // Jerusalem
};

const defaultZoom = 9;

export default function Map(props: MapProps = {}) {
  const { className, onMapReady } = props;
  const [error, setError] = useState<string | null>(null);
  const [stops, setStops] = useState<Stop[]>([]);
  const [dayLegs, setDayLegs] = useState<DayLeg[]>([]);
  const { selectedDay, selectedStopId, setSelectedStopId, setSelectedDay } = useAppState();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapKey, setMapKey] = useState(0);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key is missing. Please set VITE_GOOGLE_MAPS_API_KEY in .env');
      return;
    }
  }, []);

  useEffect(() => {
    // Load data
    Promise.all([
      loadJSON<Stop[]>('/data/dist/stops.json', { useGzip: true }),
      loadJSON<DayLeg[]>('/data/dist/day-legs.json', { useGzip: true }).catch(() => []), // Optional
    ]).then(([stopsData, dayLegsData]) => {
      setStops(stopsData);
      setDayLegs(dayLegsData || []);
    }).catch(err => {
      console.error('Failed to load data:', err);
    });
  }, []);

  // Force map re-render when selectedDay changes to clear old polylines
  useEffect(() => {
    // Force complete map re-render by changing key
    setMapKey(prev => prev + 1);
  }, [selectedDay]);

  // Filter stops and legs by selected day
  const visibleStops = useMemo(() => {
    if (!selectedDay) return stops;
    return stops.filter(stop => stop.date === selectedDay);
  }, [stops, selectedDay]);
  
  // Visible legs calculation removed - using day-based routes instead
  // const visibleLegs = useMemo(() => { ... }, [legs, visibleStops, selectedDay]);

  // Full bounds calculation removed - not currently used (was for zoom-out detection)

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    if (onMapReady) {
      onMapReady(map);
    }
    // Note: Day label centering is handled in the useEffect below (after dayLabels is defined)
  }, [onMapReady]);

  // Update bounds when stops or selectedDay changes (for "All Days" view)
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps?.LatLngBounds) return;
    
    // Only handle "All Days" view here (when no day is selected)
    if (!selectedDay) {
      const stopsToShow = stops;
      if (stopsToShow.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        stopsToShow.forEach(stop => {
          bounds.extend({ lat: stop.lat, lng: stop.lng });
        });
        // Center on bounds and set zoom to 9 (home page zoom level)
        const center = bounds.getCenter();
        mapRef.current.setCenter(center);
        mapRef.current.setZoom(9);
      }
    }
  }, [selectedDay, stops]);


  // Listen for zoom/bounds changes to detect full extent view
  // DISABLED: This was interfering with zoom level setting
  // useEffect(() => {
  //   if (!mapRef.current || !fullBounds || !selectedDay || stops.length === 0) return;

  //   const timeoutRef = { current: null as number | null };

  //   const checkBounds = () => {
  //     const map = mapRef.current;
  //     if (!map || !fullBounds) return;

  //     // Debounce the check to avoid too many calls
  //     if (timeoutRef.current !== null) {
  //       clearTimeout(timeoutRef.current);
  //     }

  //     timeoutRef.current = window.setTimeout(() => {
  //       const currentBounds = map.getBounds();
  //       if (!currentBounds) return;

  //       // Check if current bounds contain all stops (with some margin)
  //       // This means the user has zoomed out enough to see everything
  //       const margin = 0.05; // 5% margin for easier detection
  //       const ne = fullBounds.getNorthEast();
  //       const sw = fullBounds.getSouthWest();
  //       const currentNE = currentBounds.getNorthEast();
  //       const currentSW = currentBounds.getSouthWest();

  //       // Calculate if current bounds contain the full extent with margin
  //       const latRange = ne.lat() - sw.lat();
  //       const lngRange = ne.lng() - sw.lng();
        
  //       const showingFullExtent = 
  //         currentNE.lat() >= ne.lat() + (latRange * margin) &&
  //         currentSW.lat() <= sw.lat() - (latRange * margin) &&
  //         currentNE.lng() >= ne.lng() + (lngRange * margin) &&
  //         currentSW.lng() <= sw.lng() - (lngRange * margin);

  //       if (showingFullExtent && selectedDay) {
  //         // Reset to all days view
  //         setSelectedDay(null);
  //       }
  //     }, 300); // 300ms debounce
  //   };

  //   const listener = mapRef.current.addListener('bounds_changed', checkBounds);
  //   const zoomListener = mapRef.current.addListener('zoom_changed', checkBounds);

  //   return () => {
  //     if (timeoutRef.current !== null) {
  //       clearTimeout(timeoutRef.current);
  //     }
  //     if (listener) google.maps.event.removeListener(listener);
  //     if (zoomListener) google.maps.event.removeListener(zoomListener);
  //   };
  // }, [fullBounds, selectedDay, setSelectedDay, stops.length]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleMarkerClick = (stop: Stop) => {
    setSelectedStopId(stop.id);
    if (mapRef.current) {
      mapRef.current.setCenter({ lat: stop.lat, lng: stop.lng });
      mapRef.current.setZoom(14);
    }
  };

  const handleDayLabelClick = (day: string) => {
    setSelectedDay(day);
    // Center on the day label midpoint (where the day number is displayed)
    if (mapRef.current) {
      // Find the day label for this day
      const dayLabel = dayLabels.find(label => label.day === day);
      if (dayLabel) {
        mapRef.current.setCenter({
          lat: dayLabel.midpoint.lat,
          lng: dayLabel.midpoint.lng
        });
        mapRef.current.setZoom(12);
      }
    }
  };

  // Calculate midpoint of a path for label placement
  const getPathMidpoint = (path: { lat: number; lng: number }[]): { lat: number; lng: number } => {
    if (path.length === 0) return { lat: 0, lng: 0 };
    if (path.length === 1) return path[0];
    
    const midIndex = Math.floor(path.length / 2);
    return path[midIndex];
  };

  // Regular polyline paths removed - using day-based routes instead
  // const polylinePaths = useMemo(() => { ... }, [visibleLegs, visibleStops]);

  // Build day-based polyline paths with colors - STRICTLY filter by selected day
  const dayPolylinePaths = useMemo(() => {
    if (dayLegs.length === 0) {
      return [];
    }
    
    // CRITICAL: If a day is selected, ONLY return paths for that day
    if (selectedDay) {
      const matchingDayLeg = dayLegs.find(dayLeg => dayLeg.day === selectedDay);
      if (!matchingDayLeg) {
        return [];
      }
      
      const paths: Array<{
        path: { lat: number; lng: number }[];
        color: string;
        day: string;
        dayNumber: number;
      }> = [];
      
      matchingDayLeg.legs.forEach(leg => {
        // Use appropriate zoom tier based on current zoom
        const zoom = mapRef.current?.getZoom() || 10;
        const polyline = zoom < 8 
          ? leg.polyline_z6 
          : zoom < 12 
          ? leg.polyline_z10 
          : leg.polyline_z14;
        
        const path = polyline.map(([lng, lat]: number[]) => ({ lat, lng }));
        
        paths.push({
          path,
          color: matchingDayLeg.color,
          day: matchingDayLeg.day,
          dayNumber: matchingDayLeg.day_number,
        });
      });
      
      return paths;
    }
    
    // No day selected - return all paths
    const paths: Array<{
      path: { lat: number; lng: number }[];
      color: string;
      day: string;
      dayNumber: number;
    }> = [];
    
    dayLegs.forEach(dayLeg => {
      dayLeg.legs.forEach(leg => {
        // Use appropriate zoom tier based on current zoom
        const zoom = mapRef.current?.getZoom() || 10;
        const polyline = zoom < 8 
          ? leg.polyline_z6 
          : zoom < 12 
          ? leg.polyline_z10 
          : leg.polyline_z14;
        
        const path = polyline.map(([lng, lat]: number[]) => ({ lat, lng }));
        
        paths.push({
          path,
          color: dayLeg.color,
          day: dayLeg.day,
          dayNumber: dayLeg.day_number,
        });
      });
    });
    
    return paths;
  }, [dayLegs, selectedDay]);

  // Group routes by day and calculate label positions (one label per day)
  const dayLabels = useMemo(() => {
    const labelsByDay = new globalThis.Map<string, {
      day: string;
      dayNumber: number;
      color: string;
      midpoint: { lat: number; lng: number };
    }>();

    // Only process dayLegs for the selected day (or all if none selected)
    const filteredDayLegs = selectedDay 
      ? dayLegs.filter(dayLeg => dayLeg.day === selectedDay)
      : dayLegs;

    filteredDayLegs.forEach(dayLeg => {

      // Find the longest route for this day to place the label
      let longestPath: { lat: number; lng: number }[] = [];
      let longestLength = 0;

      dayLeg.legs.forEach(leg => {
        // Use z14 polyline for label placement (most detailed)
        const polyline = leg.polyline_z14;
        const path = polyline.map(([lng, lat]: number[]) => ({ lat, lng }));
        
        if (path.length > longestLength) {
          longestPath = path;
          longestLength = path.length;
        }
      });

      if (longestPath.length > 0) {
        const midpoint = getPathMidpoint(longestPath);
        labelsByDay.set(dayLeg.day, {
          day: dayLeg.day,
          dayNumber: dayLeg.day_number,
          color: dayLeg.color,
          midpoint,
        });
      }
    });

    return Array.from(labelsByDay.values());
  }, [dayLegs, selectedDay]);

  // Handle day label centering when a day is selected
  // This effect is defined AFTER dayLabels, so it can safely reference it
  useEffect(() => {
    if (!selectedDay) return;
    
    // Function to set zoom and center
    const setZoomAndCenter = () => {
      if (!mapRef.current) return;
      
      if (dayLabels.length === 0) {
        // If dayLabels not ready yet, wait and try again
        setTimeout(setZoomAndCenter, 100);
        return;
      }
      
      const dayLabel = dayLabels.find(label => label.day === selectedDay);
      
      if (dayLabel) {
        // Center on the day label midpoint (where the day number is displayed)
        mapRef.current.setCenter({
          lat: dayLabel.midpoint.lat,
          lng: dayLabel.midpoint.lng
        });
        mapRef.current.setZoom(12);
      } else {
        // Fallback: if no day label found, use bounds center
        const stopsToShow = visibleStops;
        if (stopsToShow.length > 0 && window.google?.maps?.LatLngBounds) {
          const bounds = new window.google.maps.LatLngBounds();
          stopsToShow.forEach(stop => {
            bounds.extend({ lat: stop.lat, lng: stop.lng });
          });
          const center = bounds.getCenter();
          mapRef.current.setCenter(center);
          mapRef.current.setZoom(12);
        }
      }
    };
    
    // Wait for map to be ready (especially after mapKey change causes remount)
    const timeoutId = setTimeout(() => {
      if (mapRef.current) {
        setZoomAndCenter();
      } else {
        // Map not ready yet, try again
        setTimeout(setZoomAndCenter, 100);
      }
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, [selectedDay, dayLabels, visibleStops]);

  // Accommodation polyline paths removed - using day-based routes instead
  // const accommodationPolylinePaths = useMemo(() => { ... }, [accommodationLegs]);

  if (loadError) {
    return (
      <div className="map-error">
        <p>Error loading Google Maps: {loadError.message}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-error">
        <p>{error}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="map-error">
        <p>Loading Google Maps...</p>
      </div>
    );
  }

  return (
    <div className={className} style={{ width: '100%', height: '100%' }}>
      <GoogleMap
        key={`map-${mapKey}-${selectedDay || 'all'}`}
        mapContainerStyle={mapContainerStyle}
        center={defaultCenter}
        zoom={defaultZoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {/* Day-based route polylines - different color per day */}
        {dayPolylinePaths.map((item, index) => {
          // Final safety check - should never happen if useMemo is correct
          if (selectedDay && item.day !== selectedDay) {
            return null;
          }
          return (
            <Polyline
              key={`day-route-${selectedDay || 'all'}-${item.day}-${index}`}
              path={item.path}
              options={{
                strokeColor: item.color,
                strokeOpacity: 0.8,
                strokeWeight: 4,
                zIndex: 2,
              }}
            />
          );
        })}

        {/* Day number labels on routes - clickable (only show selected day or all if none selected) */}
        {dayLabels.map((label) => {
          // Only show label if no day is selected, or if this is the selected day
          if (selectedDay && label.day !== selectedDay) {
            return null;
          }
          return (
            <Marker
              key={`day-label-${label.day}`}
              position={label.midpoint}
              label={{
                text: String(label.dayNumber),
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: label.color,
                fillOpacity: 0.9,
                strokeColor: 'white',
                strokeWeight: 2,
              }}
              zIndex={10}
              title={`Day ${label.dayNumber} - Click to filter`}
              onClick={() => handleDayLabelClick(label.day)}
              cursor="pointer"
            />
          );
        })}

        {/* Accommodation route polylines - DISABLED: using day-based routes instead */}
        {/* {accommodationPolylinePaths.map((item, index) => (
          <Polyline
            key={`accommodation-route-${index}`}
            path={item.path}
            options={{
              strokeColor: item.color,
              strokeOpacity: 0.8,
              strokeWeight: 5,
              zIndex: 2,
              icons: [
                {
                  icon: {
                    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 5,
                    strokeColor: item.color,
                    strokeWeight: 2,
                    fillColor: item.color,
                    fillOpacity: 0.9,
                  },
                  offset: '50%',
                  repeat: '150px',
                },
              ],
            }}
          />
        ))} */}

        {/* Regular route polylines - DISABLED: using day-based routes instead */}
        {/* {polylinePaths.map((path, index) => (
          <Polyline
            key={`route-${index}`}
            path={path}
            options={{
              strokeColor: '#7c3aed',
              strokeOpacity: 0.8,
              strokeWeight: 4,
              zIndex: 1,
            }}
          />
        ))} */}

        {/* Markers */}
        {visibleStops.map((stop) => {
          const isSelected = selectedStopId === stop.id;
          const dayLeg = dayLegs.find(dl => dl.day === stop.date);
          const dayColor = dayLeg?.color || '#7c3aed';
          
          return (
            <Marker
              key={stop.id}
              position={{ lat: stop.lat, lng: stop.lng }}
              title={stop.name}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: isSelected ? 10 : 8,
                fillColor: isSelected ? '#5b21b6' : dayColor,
                fillOpacity: 1,
                strokeColor: isSelected ? '#fbbf24' : 'white',
                strokeWeight: isSelected ? 3 : 2,
              }}
              onClick={() => handleMarkerClick(stop)}
            />
          );
        })}
      </GoogleMap>
    </div>
  );
}
